import { css } from '@emotion/css'
import { LoadingOverlay } from '@mantine/core'
import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { isEmpty } from '@semios/app-platform-common'
import { GridTable } from 'components/GridTable/GridTable'
import { translate } from 'i18n/i18n'
import { useMemo } from 'react'
import { detailsPanelStore } from 'stores/detailsPanelStore/detailsPanelStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import type { selectedValueGroupsStore } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { smallStore } from 'stores/smallStore/smallStore'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { isUserOnlyAFreeRegionalUser } from 'utils/isUserOnlyAFreeRegionalUser'
import { useIsRegionDataEnabled } from 'utils/useIsRegionDataEnabled'
import { useIsWideScreen } from 'utils/useIsWideScreen'
import { mergeApiArgs } from '../_utils/mergeApiArgs'
import { PropertyOrRegionNameHeader } from '../_utils/PropertyOrRegionNameHeader'
import { useApiCurrentValues } from '../_utils/useApiCurrentValues'
import { useApiValuesTimeseries } from '../_utils/useApiValuesTimeseries/useApiValuesTimeseries'
import { useTimezoneForSelectedPropertyOrRegion } from '../_utils/useTimezoneForSelectedPropertyOrRegion'
import { DetailsPanelDateRange } from '../DetailsPanelDateRange/DetailsPanelDateRange'
import { DateRangeRowSelfRegisteredUser } from '../StackemCharts/DataRangeRow/DateRangeRowSelfRegisteredUser'
import * as almondBloom from './_utils/by-domain/almondBloom/almondBloom'
import * as almondHullSplit from './_utils/by-domain/almondHullSplit/almondHullSplit'
import * as alternaria from './_utils/by-domain/alternaria/alternaria'
import * as beeHours from './_utils/by-domain/beeHours/beeHours'
import * as cherryPowderyMildewPrimaryInfectionMonitoring from './_utils/by-domain/cherryPowderyMildew/primaryInfectionMonitoring/cherryPowderyMildewPrimaryInfectionMonitoring'
import * as cherryPowderyMildewSecondaryInfectionRiskIndex from './_utils/by-domain/cherryPowderyMildew/secondaryInfectionRiskIndex/cherryPowderyMildewSecondaryInfectionRiskIndex'
import * as chill from './_utils/by-domain/chill/chill'
import * as conditions from './_utils/by-domain/conditions/conditions'
import * as dewPoint from './_utils/by-domain/dewPoint/dewPoint'
import * as evapotranspiration from './_utils/by-domain/evapotranspiration/evapotranspiration'
import * as fireBlight from './_utils/by-domain/fireBlight/fireBlight'
import * as fruitGrowth from './_utils/by-domain/fruitGrowth/fruitGrowth'
import * as grapePowderyMildewPrimaryInfectionMonitoring from './_utils/by-domain/grapePowderyMildew/primaryInfectionMonitoring/grapePowderyMildewPrimaryInfectionMonitoring'
import * as grapePowderyMildewSecondaryInfectionRiskIndex from './_utils/by-domain/grapePowderyMildew/secondaryInfectionRiskIndex/grapePowderyMildewSecondaryInfectionRiskIndex'
import * as humidity from './_utils/by-domain/humidity/humidity'
import * as insectDegreeDays from './_utils/by-domain/insectDegreeDays/insectDegreeDays'
import * as insectTrapCatches from './_utils/by-domain/insectTrapCatches/insectTrapCatches'
import * as irrigationActivity from './_utils/by-domain/irrigationActivity/irrigationActivity'
import * as leafWetness from './_utils/by-domain/leafWetness/leafWetness'
import * as plantStress from './_utils/by-domain/plantStress/plantStress'
import * as precipitation from './_utils/by-domain/precipitation/precipitation'
import * as soil from './_utils/by-domain/soil/soil'
import * as sprayConditions from './_utils/by-domain/sprayConditions/sprayConditions'
import * as temperature from './_utils/by-domain/temperature/temperature'
import * as wetBulb from './_utils/by-domain/wetBulb/wetBulb'
import * as wind from './_utils/by-domain/wind/wind'
import * as windmachine from './_utils/by-domain/windMachine/windMachine'
import { createColumns } from './_utils/createColumns'
import { createContent } from './_utils/createContent'

export const SummaryGridTable = ({
  containerHeight,
  containerWidth,
  preventFetch,
  selectedValueGroups,
}: {
  containerHeight: number
  containerWidth: number
  preventFetch: boolean
  selectedValueGroups: ReturnType<typeof selectedValueGroupsStore.getState>['selectedValueGroups']
}) => {
  const isWideScreen = useIsWideScreen()
  const selectedFieldAssets = selectedFieldAssetsStore.useSelector((s) => s)
  const timezone = useTimezoneForSelectedPropertyOrRegion()

  // selectedTab is left out on purpose: switching tabs must not rebuild this table, which stays mounted while hidden
  const { dateFrom, dateTo, detailsPanelStoreContents, detailsPanelStoreContentsThatShouldTriggerARefetch } =
    detailsPanelStore.useSelector(({ selectedTab, ...s }) => ({
      dateFrom: s.dateFrom,
      dateTo: s.dateTo,
      detailsPanelStoreContents: s,
      detailsPanelStoreContentsThatShouldTriggerARefetch: {
        keyForRefreshing: s.keyForRefreshing,
        keyForRefreshingSummaryTab: s.keyForRefreshingSummaryTab,
      },
    }))

  const { userDetailsStoreContentsThatShouldRerenderGridTable } = userDetailsStore.useSelector((s) => {
    const { volume, flowRate } = s

    return {
      userDetailsStoreContentsThatShouldRerenderGridTable: {
        volume,
        flowRate,
      },
    }
  })

  const { appliedIrrigationActivityDisplay } = smallStore.useSelector((s) => ({
    appliedIrrigationActivityDisplay: s.appliedIrrigationActivityDisplay,
  }))

  const showRegionalData = useIsRegionDataEnabled()

  const commonApiParams = {
    selectedValueGroups,
    selectedFieldAssets,
  }

  const mergedApiArgs = mergeApiArgs(
    almondBloom.apiArgs(commonApiParams),
    almondHullSplit.apiArgs(commonApiParams),
    alternaria.apiArgs(commonApiParams),
    beeHours.apiArgs(commonApiParams),
    cherryPowderyMildewPrimaryInfectionMonitoring.apiArgs(commonApiParams),
    cherryPowderyMildewSecondaryInfectionRiskIndex.apiArgs(commonApiParams),
    chill.apiArgs(commonApiParams),
    conditions.apiArgs(commonApiParams),
    dewPoint.apiArgs(commonApiParams),
    evapotranspiration.apiArgs(commonApiParams),
    fireBlight.apiArgs(commonApiParams),
    fruitGrowth.apiArgs(commonApiParams),
    grapePowderyMildewPrimaryInfectionMonitoring.apiArgs(commonApiParams),
    grapePowderyMildewSecondaryInfectionRiskIndex.apiArgs(commonApiParams),
    humidity.apiArgs(commonApiParams),
    insectDegreeDays.apiArgs(commonApiParams),
    insectTrapCatches.apiArgs(commonApiParams),
    leafWetness.apiArgs(commonApiParams),
    irrigationActivity.apiArgs(commonApiParams),
    plantStress.apiArgs(commonApiParams),
    precipitation.apiArgs(commonApiParams),
    soil.apiArgs(commonApiParams),
    sprayConditions.apiArgs(commonApiParams),
    temperature.apiArgs(commonApiParams),
    wetBulb.apiArgs(commonApiParams),
    wind.apiArgs(commonApiParams),
    windmachine.apiArgs(commonApiParams),
  )

  const currentValuesApiArgs = irrigationActivity.currentValuesApiArgs(commonApiParams)

  // we are fetching current values from irrigation activity only
  // currentValuesApiArgs will be null for all other cases to prevent fetch
  const { data: currentValuesData } = useApiCurrentValues({
    args: currentValuesApiArgs || {},
    preventFetch: isEmpty(currentValuesApiArgs) || preventFetch,
  })

  const { data, loading, updateData } = useApiValuesTimeseries({
    args: { dateFrom, dateTo, ...mergedApiArgs },
    preventFetch,
    watchers: [JSON.stringify(detailsPanelStoreContentsThatShouldTriggerARefetch)],
  })

  const content = useMemo(
    () =>
      createContent({
        data: data as routes.Values.Response,
        selectedValueGroups,
        selectedFieldAssets,
        currentValuesData,
        updateData,
        isWideScreen,
      }),
    [
      data,
      selectedFieldAssets,
      selectedValueGroups,
      JSON.stringify(detailsPanelStoreContents),
      JSON.stringify(userDetailsStoreContentsThatShouldRerenderGridTable),
      appliedIrrigationActivityDisplay,
      showRegionalData,
      currentValuesData,
    ],
  )

  const columns = useMemo(
    () => createColumns({ content, dateFrom, dateTo, timezone }),
    [
      content,
      dateFrom,
      dateTo,
      timezone,
      JSON.stringify(userDetailsStoreContentsThatShouldRerenderGridTable),
    ],
  )

  const isUserFreeRegionalUser = isUserOnlyAFreeRegionalUser()

  return (
    <div
      className={css({
        width: containerWidth,
        height: containerHeight,
        maxHeight: containerHeight,
        overflow: 'auto',
        touchAction: 'pan-x pan-y', //disable pinch to zoom to avoid mobile view bug with the map
      })}
    >
      <LoadingOverlay visible={loading} />
      <GridTable
        childrenUpper={
          <div css={{ display: 'flex', flexDirection: 'column' }}>
            <PropertyOrRegionNameHeader />
            {!isUserFreeRegionalUser ? (
              <>
                <DetailsPanelDateRange selectedValueGroups={selectedValueGroups} />
              </>
            ) : (
              <DateRangeRowSelfRegisteredUser selectedValueGroups={selectedValueGroups} />
            )}
          </div>
        }
        columns={columns}
        containerWidth={containerWidth}
        content={content}
        key={selectedFieldAssets.property}
        topLeftCell={{ title: translate.phrases.banyanApp('Scroll horizontally to see more dates') }}
      />
    </div>
  )
}
